import { Loading } from '@byecode/ui'
import {
    APPLICATION_VERSION_PROD,
    DEFAULT_DESKTOP_BREAK_POINT,
    FillPickerProviderWithStorage,
    isMobileDevice,
    SpaceQuotaProvider,
    UploadManage,
    useAtomAction,
    useAtomData
} from '@lighthouse/shared'
import React, { useCallback, useMemo, useRef } from 'react'
import { Outlet, useMatch, useParams } from 'react-router-dom'
import { useAsyncRetry, useUpdateEffect } from 'react-use'
import styled from 'styled-components'
import { useImmer } from 'use-immer'

import {
    fetchAppEnvListAtom,
    fetchApplicationAtom,
    fetchApplicationListAtom,
    fetchBreakPointsAtom,
    fetchOtherPlatformsAtom,
    fetchSyncComponentsAtom,
    fetchTemplateBlockAtom,
    setLanguageAtom,
    setMainBreakPointAtom
} from '@/atoms/application/action'
import { developerRole } from '@/atoms/application/constants'
import { applicationSettingAtom, appRoleAtom, currentAppVersionIdAtom, currentEnvIdAtom } from '@/atoms/application/state'
import { fetchDataSourceListAtom, fetchSystemDataSourceAtom } from '@/atoms/dataSource/action'
import { fetchPageListAtom } from '@/atoms/page/action'
import { fetchWorkflowListAtom } from '@/atoms/workflow/action'
import { ApplicationContainer } from '@/containers/Application'
import { FindUse } from '@/containers/FindUse'
import { MobileSupport } from '@/containers/MobileSupport'
import { OtherPlatformEvent } from '@/containers/OtherPlatforms/OtherPlatformEvent'
import type { FindUseConfigure } from '@/contexts/PageContext'
import { FindUseProvider } from '@/contexts/PageContext'
import http from '@/http'
import { useSpaceQuota } from '@/shared/reusable'
import Page from '@/views/Byecode/Application/[id]/Page'

const SCxApplicationContent = styled.div`
    flex: 1;
    height: 100%;
    overflow: hidden;
`

const Application: React.FC = () => {
    const [state, setState] = useImmer<FindUseConfigure>({ open: false })
    const { applicationId: pathAppId = '' } = useParams()
    const { data, update } = useSpaceQuota()
    const { run: setAppRole } = useAtomAction(appRoleAtom)
    const { run: fetchApplication } = useAtomAction(fetchApplicationAtom)
    const { run: fetchTemplateBlock } = useAtomAction(fetchTemplateBlockAtom)
    const { run: fetchSystemDataSource } = useAtomAction(fetchSystemDataSourceAtom)
    const { run: fetchPageList } = useAtomAction(fetchPageListAtom)
    const { run: fetchAppEnvList } = useAtomAction(fetchAppEnvListAtom)
    const { run: fetchDataSourceList } = useAtomAction(fetchDataSourceListAtom)
    const { run: fetchApplicationList } = useAtomAction(fetchApplicationListAtom)
    const { run: fetchSyncComponents } = useAtomAction(fetchSyncComponentsAtom)
    const { run: fetchBreakPoints } = useAtomAction(fetchBreakPointsAtom)
    const { run: setLanguage } = useAtomAction(setLanguageAtom)
    const { run: setMainBreakPoint } = useAtomAction(setMainBreakPointAtom)
    const { run: fetchOtherPlatforms } = useAtomAction(fetchOtherPlatformsAtom)
    const { run: fetchWorkflowList } = useAtomAction(fetchWorkflowListAtom)
    const { open, config } = state
    // 使用缓存的设备类型判断（用于检测是否在移动设备上访问 Builder）
    const isMobile = isMobileDevice()

    const appVersionId = useAtomData(currentAppVersionIdAtom)
    const appEnvId = useAtomData(currentEnvIdAtom)
    const prefVersionIdRef = useRef(appVersionId)
    const prevEnvIdRef = useRef(appEnvId)

    const getApplicationData = useCallback(async () => {
        const app = await fetchApplication()
        setAppRole(developerRole.userId)
        setMainBreakPoint(DEFAULT_DESKTOP_BREAK_POINT)
        setLanguage(app.config.language.list?.[0].lang)
        prefVersionIdRef.current = app.version.id
        prevEnvIdRef.current = app.version.envId
        await Promise.all([
            fetchAppEnvList(),
            fetchSystemDataSource(),
            fetchPageList(),
            fetchSyncComponents(),
            fetchDataSourceList({ appId: pathAppId, envId: app.version.envId }),
            fetchOtherPlatforms(),
        ])
        // 必须在 fetchApplication 写完 B-App-Id 之后拉，否则会把上一个应用的流程写进来
        fetchWorkflowList()
        fetchBreakPoints()
        fetchTemplateBlock()
        fetchApplicationList(app.spaceId)
        return app
    }, [
        fetchAppEnvList,
        fetchApplication,
        fetchApplicationList,
        fetchBreakPoints,
        fetchDataSourceList,
        fetchOtherPlatforms,
        fetchPageList,
        fetchSyncComponents,
        fetchWorkflowList,
        fetchSystemDataSource,
        fetchTemplateBlock,
        pathAppId,
        setAppRole,
        setLanguage,
        setMainBreakPoint
    ])

    const { loading, value, retry, error } = useAsyncRetry(async () => {
        http.defaults.headers.common['B-App-User-Id'] = developerRole.userId
        http.defaults.headers.common['B-Lang'] = ''
        const app = await getApplicationData()
        return app.id
    }, [getApplicationData])

    useUpdateEffect(() => {
        if (!appVersionId || error) {
            return
        }
        // if (!prefVersionIdRef.current) {
        //     prefVersionIdRef.current = appVersionId
        //     prevEnvIdRef.current = appEnvId
        //     return
        // }

        if (appVersionId !== prefVersionIdRef.current) {
            retry()
        }
    }, [appVersionId, retry])

    useUpdateEffect(() => {
        if (!appEnvId) {
            return
        }
        if (!prefVersionIdRef.current) {
            prevEnvIdRef.current = appEnvId
            return
        }

        if (appEnvId !== prevEnvIdRef.current) {
            retry()
        }
    }, [appEnvId, retry])

    const isLayoutPath = useMatch('/:appId/page')

    const handleChangeFindUse = useCallback(
        (val: FindUseConfigure) => {
            setState(val)
        },
        [setState]
    )

    const palettes = useAtomData(
        applicationSettingAtom,
        useCallback(s => s?.theme.palettes ?? [], [])
    )

    const quotaValue = useMemo(
        () => ({
            quota: data,
            update
        }),
        [data, update]
    )

    const loadingDescription = useMemo(() => {
        if (prefVersionIdRef.current !== appVersionId) {
            return `正在切换${appVersionId === APPLICATION_VERSION_PROD ? '正式' : '草稿'}版本`
        }
        if (prevEnvIdRef.current !== appEnvId) {
            return '正在切换环境'
        }
        return undefined
    }, [appEnvId, appVersionId])

    return useMemo(() => {
        if (isMobile) {
            return <MobileSupport />
        }
        // 初次加载时显示全局loading
        if (!value && loading) {
            return <Loading />
        }
        if (!value) {
            return null
        }

        return (
            <SpaceQuotaProvider value={quotaValue}>
                <FillPickerProviderWithStorage palettes={palettes}>
                    <OtherPlatformEvent>
                        <ApplicationContainer id={value}>
                            <FindUseProvider value={{ open, config, onChange: handleChangeFindUse }}>
                                <SCxApplicationContent>
                                    {/* 更新应用时显示局部loading */}
                                    {prefVersionIdRef.current === appVersionId && prevEnvIdRef.current === appEnvId ? (
                                        <div
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                transition: 'none', // 防止 opacity 变化有动画延迟
                                                // 使用 opacity + position 替代 display:none，保持元素可计算尺寸
                                                // 避免路由切换后 vh/vw 单位的 block 因获取不到模拟断点盒子高度而坍缩
                                                ...(isLayoutPath
                                                    ? undefined
                                                    : {
                                                          opacity: 0,
                                                          visibility: 'hidden',
                                                          position: 'absolute',
                                                          pointerEvents: 'none'
                                                      })
                                            }}
                                        >
                                            {/* 不离开应用的话，页面会一直保持渲染状态 */}
                                            <Page />
                                        </div>
                                    ) : (
                                        <Loading shape="indicator" description={loadingDescription} />
                                    )}
                                    <Outlet context={value} />
                                </SCxApplicationContent>
                                {open && <FindUse />}
                            </FindUseProvider>
                            <UploadManage />
                        </ApplicationContainer>
                    </OtherPlatformEvent>
                </FillPickerProviderWithStorage>
            </SpaceQuotaProvider>
        )
    }, [
        appEnvId,
        appVersionId,
        config,
        handleChangeFindUse,
        isLayoutPath,
        isMobile,
        loading,
        loadingDescription,
        open,
        palettes,
        quotaValue,
        value
    ])
}

export default Application
